import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Table } from "react-bootstrap";
import "./License.css";
import "../../../../../common/styles/table.css";
import TablePagination from "../../../TeacherAdmin/pagination/TablePagination";
import { useGetLicenseOverview } from "../../../../../hooks/school/licenses/useGetLicenseOverview";
import DashboardStats from "../../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../../common/Dashboard/DashboardStatsSkeleton";
import TableSkeleton from "../../../common/TableSkeleton";
import { useTenantAuth } from "../../../../../context/TeanantAuthProvider";

/**
 * SchoolLicenses component - School-specific licenses dashboard
 * Displays license statistics and activity table for schools
 * @returns {JSX.Element}
 */
export const SchoolLicenses = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { currentUser } = useTenantAuth();

  // Use the school license overview hook
  const { licenseData, loading, error, getLicenseOverview } =
    useGetLicenseOverview();

  // Fetch license data on component mount
  useEffect(() => {
    getLicenseOverview();
  }, []);

  // Pagination settings
  const totalPages = 1; // Update this when integrating with paginated API

  /**
   * Format date string to readable format
   * @param {string} dateString
   * @returns {string}
   */
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const options = { day: "numeric", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-IN", options);
  };

  /**
   * Format amount to currency
   * @param {string|number} amount
   * @param {string} currency
   * @returns {string}
   */
  const formatAmount = (amount, currency) => {
    if (!amount) return "-";
    const numAmount = parseFloat(amount);
    return `${numAmount.toLocaleString("en-IN")} ${currency}`;
  };

  /**
   * Get status badge class
   * @param {string} status
   * @returns {string}
   */
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "complete":
      case "success":
        return "status-success";
      case "partial":
        return "status-pending";
      case "failed":
        return "status-danger";
      default:
        return "status-pending";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="">
        <div className="">
          {/* Header */}
          <div className="license-header">
            <h1 className="license-title">Licenses</h1>
          </div>

          {/* Loading skeleton for stats */}
          <DashboardStatsSkeleton count={3} />

          {/* Loading skeleton for table */}
          <TableSkeleton rows={5} columns={7} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="">
        <div className="">
          {/* Header */}
          <div className="license-header">
            <h1 className="license-title">Licenses</h1>
          </div>

          {/* Error Alert */}
          <div className="alert alert-danger mt-3" role="alert">
            <div className="d-flex align-items-center justify-content-between">
              <span>
                <i className="fa-solid fa-circle-exclamation me-2"></i>
                {error}
              </span>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={getLicenseOverview}
              >
                <i className="fa-solid fa-rotate-right me-1"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get stats from API data
  const licenseStats = {
    total: licenseData?.stats?.total_licenses ?? 0,
    used: licenseData?.stats?.used_licenses ?? 0,
    available: licenseData?.stats?.available_licenses ?? 0,
  };

  // Get activities from API data
  const activities = licenseData?.activity || [];

  // Prepare stats for DashboardStats component
  const licenseStatsData = [
    {
      label: "Total Licenses",
      value: licenseStats.total.toLocaleString(),
      icon: "fa-id-card",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Used Licenses",
      value: licenseStats.used.toLocaleString(),
      icon: "fa-user-check",
      colorClass: "dashboard-stat-card-purple",
    },
    {
      label: "Available Licenses",
      value: licenseStats.available.toLocaleString(),
      icon: "fa-users",
      colorClass: "dashboard-stat-card-peach",
    },
  ];

  const isRoleSelf = currentUser?.auth?.tenant_type || "";

  return (
    <div className="">
      <div className="">
        {/* Stats Cards using DashboardStats */}
        <DashboardStats stats={licenseStatsData} />

        {/* Activity Table Header */}
        <div className="license-activity-header">
          <h2 className="license-activity-title">License Activity Table</h2>
          {isRoleSelf === "self" && (
            <Link
              to="/school-admin/settings/purchase-licenses?license-type=add-on"
              className="purchase-license-btn"
            >
              + Purchase Licenses
            </Link>
          )}
        </div>

        {/* Activity Table */}
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Licenses Added</th>
                  <th>Expiry Date</th>
                  <th>Amount</th>
                  <th>Payment Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {activities.length > 0 ? (
                  activities.map((activity, index) => (
                    <tr key={index}>
                      <td>{formatDate(activity.date)}</td>
                      <td>{activity.action || "-"}</td>
                      <td>
                        {activity.licenses_added?.toLocaleString() || "-"}
                      </td>
                      <td>{formatDate(activity.expiry_date)}</td>
                      <td>{formatAmount(activity.amount, activity.currency)}</td>
                      <td>{activity.payment_type || "-"}</td>
                      <td>
                        <span className={getStatusClass(activity.status)}>
                          {activity.status
                            ? activity.status.charAt(0).toUpperCase() +
                            activity.status.slice(1)
                            : "-"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className="text-center m-5">
                        No activities found.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={false}
          />
        </div>
      </div>
    </div>
  );
};

export default SchoolLicenses;
